import type { ComponentOverride } from '../types';

/**
 * Brand web fonts.
 *
 * Two deliberate differences from the upstream theme in the tdc-managed-cloud repo:
 *
 * 1. The files are served from our own origin (web/public/fonts) rather than https://s.c.dk.
 *    Our Content-Security-Policy is `font-src 'self' data:`, so a third-party host would be
 *    blocked outright — and a tool that measures somebody else's vendor dependency should not
 *    quietly reach out to a vendor CDN on every page load.
 *
 * 2. Upstream declares these under a `fallbacks` key. That is JSS syntax from MUI v4; on MUI 5
 *    and later, styles go through emotion, where `fallbacks` is neither a CSS property nor a
 *    selector, so the whole block is dropped and the browser silently falls back to Helvetica.
 *    Multiple @font-face rules have to be given as a style string, which is what this is.
 */
const latin =
  'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD';
const latinExt =
  'U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF';
const gurmukhi = 'U+0964-0965, U+0A01-0A75, U+200C-200D, U+20B9, U+25CC, U+262C, U+A830-A839';

/** Danish needs æ, ø and å, which live in the latin and latin-ext ranges. */
function muktaMahee(weight: number): string {
  return [
    ['latin', latin],
    ['latin-ext', latinExt],
    ['gurmukhi', gurmukhi],
  ]
    .map(
      ([subset, range]) => `
  @font-face {
    font-family: 'Mukta Mahee';
    font-style: normal;
    font-display: swap;
    font-weight: ${weight};
    src: url('/fonts/mukta-mahee-${subset}-${weight}-normal.woff2') format('woff2'),
         url('/fonts/mukta-mahee-all-${weight}-normal.woff') format('woff');
    unicode-range: ${range};
  }`,
    )
    .join('\n');
}

function tdc(weight: number, file: string): string {
  return `
  @font-face {
    font-family: 'TDC';
    font-style: normal;
    font-display: swap;
    font-weight: ${weight};
    src: url('/fonts/${file}') format('woff2');
  }`;
}

/**
 * Exported on its own because MuiCssBaseline.styleOverrides is a single value: when the app
 * layers its own baseline rules on top, createTheme replaces this string rather than merging
 * with it, and the fonts vanish. Composing the two explicitly is the way to keep both.
 */
export const tdcFontFaces: string = [
  tdc(100, 'TDC_lite.woff2'),
  tdc(400, 'TDC_regular.woff2'),
  tdc(700, 'TDC_bold.woff2'),
  muktaMahee(300),
  muktaMahee(500),
  muktaMahee(600),
].join('\n');

export const MuiCssBaseline: ComponentOverride<'MuiCssBaseline'> = {
  MuiCssBaseline: {
    styleOverrides: tdcFontFaces,
  },
};
